import type { FixedAssetResources } from './en-US.js';

export const ja: FixedAssetResources = {
  nav: {
    myAssets: '自分の資産',
    assets: '資産一覧',
    locations: '保管場所',
    inventories: '棚卸',
    import: 'インポート',
  },
  common: {
    none: '—',
    unknown: '不明',
    all: 'すべて',
    save: '保存',
    saved: '保存しました。',
    reload: '再読み込み',
    edit: '編集',
    add: '追加',
    active: '使用中',
    inactive: '使用停止',
    deactivate: '使用を停止',
    activate: '使用を再開',
  },
  field: {
    code: '資産ID',
    name: '名称',
    type: '資産種別',
    accountTitle: '勘定科目',
    assetClass: '資産区分',
    tangibility: '有形/無形',
    site: '拠点',
    location: '保管場所',
    user: '使用者',
    manager: '資産管理者',
    status: '状態',
    acquiredOn: '取得日',
    currency: '通貨',
    amount: '取得価額',
    amountJpy: '円建て取得価額',
    usefulLifeYears: '耐用年数',
    maker: 'メーカー',
    model: '型番',
    serialNumber: 'シリアル番号',
    previousCode: '旧資産番号',
    notes: '備考',
    disposedOn: '除却日',
    disposalMethod: '除却方法',
    disposalNote: '除却メモ',
  },
  myAssets: {
    title: '自分の資産',
    empty: '使用中の資産はありません。',
    inventoryBanner: '棚卸「{{title}}」を実施中です。手元にある資産を確認してください。',
    confirm: '確認する',
    confirmed: '確認済み',
  },
  assets: {
    title: '資産一覧',
    register: '資産を登録',
    export: 'CSV出力',
    search: '検索',
    searchPlaceholder: '資産ID・名称・メーカー・型番・シリアル番号',
    allSites: 'すべての拠点',
    allTypes: 'すべての資産種別',
    openStatuses: '除却済み以外',
    jpyMissing: '円建て取得価額なし',
    userDisabled: '使用者が無効',
    empty: '該当する資産はありません。',
    printLabels: 'ラベル印刷（{{selected}}件）',
    clearSelection: '選択を解除',
    selectPage: 'このページをすべて選択',
    selectionLimit: 'ラベル印刷で選択できるのは {{max}} 件までです。',
  },
  assetForm: {
    registerTitle: '資産を登録',
    editTitle: '資産を編集',
    register: '登録',
    registerAnother: '同じ内容で続けて登録',
    registered: '{{code}} を登録しました。',
    jpyHint: '日本の拠点で円以外の通貨のときは必須です。',
    siteNotConfigured: '未設定です（拠点の国と通貨は管理者が設定します）。',
    noUser: '使用者なし',
    chooseUser: '使用者を選択',
  },
  asset: {
    details: '詳細',
    history: '履歴',
    actions: '操作',
    changeUser: '使用者を変更',
    changeManager: '資産管理者を変更',
    changeLocation: '保管場所を変更',
    changeSite: '別の拠点へ移動',
    changeStatus: '状態を変更',
    dispose: '除却',
    revertDisposal: '除却を取り消し',
    confirmRevert: '{{code}} の除却を取り消しますか？除却前の状態に戻ります。',
    conflict: 'この資産はほかの場所で変更されました。再読み込みして最新の状態を確認してください。',
    jpyMissing: '円建て取得価額がありません（日本の拠点では必須です）。',
    userDisabled:
      '使用者のアカウントは mai ID で無効になっています。別の使用者を指定してください。',
    noUser: '使用者なし',
    noLocation: '保管場所なし',
    hidden: '非表示',
    disposalNoteHint: '「その他」のときは必須です。',
    attachments: '添付ファイル',
    printLabel: 'ラベル印刷',
    confirmIn: '「{{title}}」で確認',
    confirmedIn: '「{{title}}」で確認済み',
  },
  history: {
    empty: '履歴はまだありません。',
    periods: '使用期間',
    period: '{{from}} – {{to}}',
    current: '現在',
    by: '{{name}}',
    system: 'システム',
  },
  card: {
    title: '資産 {{code}}',
    returnHint: 'この資産を見つけた方は、資産管理者へお返しください。',
    open: '資産を開く',
  },
  attachments: {
    photos: '写真',
    documents: '書類',
    addPhoto: '写真を追加',
    addDocument: '書類を追加',
    hint: 'JPEG・PNG・WebP・PDF（{{maxMiB}} MBまで）。写真はアップロード前に縮小されます。',
    full: '添付ファイルが上限の {{max}} 件に達しています。',
    uploading: 'アップロード中…',
    empty: '添付ファイルはまだありません。',
    remove: '添付ファイルを削除',
    removeNamed: '{{name}} を削除',
    confirmRemove: '{{name}} を削除しますか？表示されなくなりますが、記録として保管されます。',
  },
  labels: {
    title: 'QRラベル印刷',
    print: '印刷',
    layout: 'ラベル用紙',
    layoutA4: 'A4シート 24面（70 × 37 mm）',
    layoutSingle: 'ラベルプリンター（62 × 29 mm）',
    start: '開始位置',
    startHint: '使用済みのラベルを飛ばします（1 = 左上）。',
    summary: 'ラベル {{labels}} 枚・{{sheets}} ページ。',
    printHint: '印刷画面では倍率100%・余白なしを選んでください。',
    missing: '見つからない資産ID：{{codes}}',
    noneSelected: '資産が選択されていません。資産一覧で選択してください。',
    toAssets: '資産一覧へ',
  },
  inventories: {
    title: '棚卸',
    start: '棚卸を開始',
    titleField: 'タイトル',
    scope: '対象の保管場所',
    wholeSite: '拠点全体',
    startHint:
      '開始時点の拠点（または選んだ保管場所）の資産が対象です。後から登録した資産は含まれません。',
    empty: '棚卸はまだありません。',
    startedAt: '開始',
    closedAt: '完了',
    progress: '{{total}}件中 {{checked}}件確認',
  },
  inventory: {
    close: '棚卸を完了',
    confirmClose:
      '「{{title}}」を完了しますか？未確認の資産は未確認のまま残り、資産の情報は変更されません。',
    export: '結果をCSV出力',
    expectedLocation: '登録上の保管場所',
    expectedUser: '登録上の使用者',
    result: '棚卸結果',
    foundLocation: '確認場所',
    locationDiffers: '保管場所の相違',
    onlyDiffers: '保管場所の相違のみ',
    selfConfirmed: '使用者による確認',
    checked: '確認者',
    note: 'メモ',
    check: '確認',
    checkTitle: '{{code}} を確認',
    found: '確認済み',
    search: '資産IDまたは名称',
    closedNotice:
      'この棚卸は完了しています。所在不明の資産を紛失にする、確認場所へ移動するなどの対応は各資産のページで行ってください。',
  },
  import: {
    title: 'CSVインポート',
    intro:
      'テンプレートを表計算ソフトで記入し、CSV形式で保存してください。資産種別はプレフィックス、拠点は名称、使用者と資産管理者は社員番号で指定します。見出しや選択肢は4言語のどれでも読み込めます。',
    template: 'テンプレートをダウンロード',
    chooseFile: 'CSVファイルを選択',
    encoding: '文字コード',
    encodings: {
      utf8: 'UTF-8',
      shiftJis: 'Shift_JIS（日本語版Excel）',
      gb18030: 'GB18030（簡体字中国語版Excel）',
      big5: 'Big5（繁体字中国語版Excel）',
    },
    createLocations: '存在しない保管場所を作成する',
    preview: 'ファイルを確認',
    valid: '問題はありません。{{rows}} 件の資産を登録します。',
    invalid:
      '{{issues}} 件の問題があります。ファイルを修正して再度確認してください。問題がある間は何も登録されません。',
    missingLocations: '存在しない保管場所：{{names}}',
    commit: '{{rows}} 件を登録',
    done: '{{rows}} 件の資産を登録しました。',
    locationsCreated: '保管場所を {{created}} 件作成しました。',
    downloadCodes: '新しい資産IDをダウンロード（CSV）',
    row: '行',
    column: '列',
    problem: '問題',
    columns: {
      userEmployeeNumber: '使用者の社員番号',
      managerEmployeeNumber: '資産管理者の社員番号',
    },
    problems: {
      Required: '必須です',
      Invalid: '値が正しくありません',
      TooLong: '長すぎます',
      UnknownAssetType: 'このプレフィックスまたは名称の資産種別はありません',
      InactiveAssetType: 'この資産種別は使用停止中です',
      UnknownSite: 'この名称の拠点はありません',
      SiteNotConfigured: '拠点の国と通貨が設定されていません',
      UnknownLocation: '拠点にこの保管場所はありません',
      InactiveLocation: 'この保管場所は使用停止中です',
      UnknownEmployee: 'この社員番号のユーザーはいません',
      UserDisabled: 'ユーザーのアカウントが無効です',
      NotAssetManager: '資産管理者ではありません',
      StatusNotAllowed: '登録できる状態は使用中・保管中・修理中です',
      FutureDate: '未来の日付です',
      InvalidCurrency: '通貨コードが正しくありません',
      InvalidAmount: '通貨に対して金額が正しくありません',
      JpyAmountRequired: '日本の拠点では円建て取得価額が必須です',
      AssetNumberExhausted: 'この資産種別の資産IDが上限に達しています',
      UnknownColumn: '不明な列です',
      DuplicateColumn: '列が重複しています',
      MissingColumn: '必須の列がありません',
    },
  },
  locations: {
    title: '保管場所',
    add: '保管場所を追加',
    empty: 'この拠点の保管場所はまだありません。',
  },
  assetTypes: {
    title: '資産種別',
    description:
      '資産種別ごとに、資産IDの先頭になる1〜4文字のプレフィックスを設定します（例: PC0000000001）。資産IDを発行した後はプレフィックスを変更できません。',
    add: '資産種別を追加',
    prefix: 'プレフィックス',
    prefixHint: '英字A〜Zで1〜4文字',
    prefixLocked: '資産IDを発行済みのため、プレフィックスは変更できません。',
    defaultAssetClass: '資産区分の初期値',
    defaultTangibility: '有形/無形の初期値',
    defaultUsefulLife: '耐用年数の初期値',
    empty: '資産種別はまだありません。',
  },
  sites: {
    title: '拠点',
    description:
      'mai ID のグループがそれぞれ拠点になります。国と既定の通貨を設定すると、その拠点で資産を登録できます。日本の拠点では円建て取得価額も必須です。',
    country: '国',
    defaultCurrency: '既定の通貨',
    notConfigured: '未設定',
    jpyRequired: '円建て取得価額が必須',
    set: '設定',
  },
  userPicker: {
    search: '氏名・メールアドレス・社員番号',
  },
  assetManagers: {
    title: '資産管理者',
    description:
      '資産管理者は、すべての拠点の資産と保管場所を登録・変更し、使用者を指定できます。管理者は常にこれらの操作ができます。',
    add: '資産管理者を追加',
    name: '氏名',
    employeeNumber: '社員番号',
    site: '拠点',
    grantedAt: '追加日時',
    managedAssets: '管理中の資産',
    remove: '削除',
    confirmRemove: '{{name}} を資産管理者から外しますか？',
    empty: '資産管理者は追加されていません。',
    unknownUser: '不明なユーザー',
    handOver: '資産を引き継ぐ',
    handOverTo: '引き継ぎ先の資産管理者',
    handedOver: '{{count}} 件の資産を引き継ぎました。',
  },
  audit: {
    title: '監査ログ',
    time: '日時',
    actor: '実行者',
    action: '操作',
    target: '対象',
    system: 'システム',
    actions: {
      AssetTypeChanged: '資産種別の変更',
      SiteSettingsChanged: '拠点設定の変更',
      StorageLocationChanged: '保管場所の変更',
      AssetManagerGranted: '資産管理者の追加',
      AssetManagerRevoked: '資産管理者の削除',
      AssetDisposalReverted: '除却の取り消し',
      AssetsImported: '資産のインポート',
      AssetCsvExported: '資産CSVの出力',
      InventoryStarted: '棚卸の開始',
      InventoryClosed: '棚卸の完了',
      LocalAdminGranted: '管理者の追加',
      LocalAdminRevoked: '管理者の削除',
    },
  },
};
